import { useMemo, useState } from "react";
import { useUser, SignOutButton } from "@clerk/clerk-react";
import { Card, CardBody, CardHeader } from "@heroui/card";
import { Avatar } from "@heroui/avatar";
import { Button } from "@heroui/button";
import { Chip } from "@heroui/chip";
import { Divider } from "@heroui/divider";
import { Mail, CalendarClock, User2, LogOut, ShieldCheck } from "lucide-react";
import DefaultLayout from "@/layouts/default";
import TabsComp from "@/components/tabs-comp";
import { ThemeSwitch } from "@/layouts/theme-switch";
import MyConnectionsComp from "@/components/members/my-connections-comp";
import LogoutDrawer from "@/components/drawers/logout-drawer";

const InfoRow = ({ icon: Icon, label, children, mono = false }) => (
  <div
    className="
      flex items-start gap-3 rounded-xl
      border border-neutral-200/80 dark:border-default-200/60
      bg-white dark:bg-content1/50
      p-3 shadow-sm dark:shadow-none
    "
  >
    <div
      className="
        mt-0.5 rounded-lg
        border border-neutral-200/80 dark:border-default-200/60
        p-1.5 bg-neutral-50 dark:bg-transparent
      "
    >
      <Icon className="h-4 w-4 text-neutral-700 dark:opacity-80" aria-hidden="true" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs uppercase tracking-wider text-neutral-500 dark:opacity-60">
        {label}
      </div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`}>{children}</div>
    </div>
  </div>
);

/* ---------- Page ---------- */
export default function ProfilePage() {
  const { user } = useUser();
  const [selectedTab, setSelectedTab] = useState("profile");

  const tabs = useMemo(
    () => [
      { key: "profile", title: "Profile" },
      { key: "connections", title: "Connections" },
    ],
    []
  );

  if (!user) return null;

  const fullName = user.fullName || user.username || "Your Profile";
  const email = user.primaryEmailAddress?.emailAddress ?? "—";
  const created = new Date(user.createdAt).toLocaleString();
  const avatarSeed = encodeURIComponent(fullName);
  const roleLabel = user?.publicMetadata?.role || "Member";
  const [showLogOut, setShowLogout] = useState(false);

  const isActive = (key) => selectedTab === key;

  function onLogoutDrawerResp(_cmd, _value){
    if(_cmd === 'close'){
      setShowLogout(false)
    }
  }

  // shared card shell for tabs
  const cardShell =
    "rounded-3xl border border-neutral-200/80 dark:border-default-200/60 backdrop-blur-0 dark:backdrop-blur-xl";

  return (
    <DefaultLayout>
      <div className="px-4 sm:px-0 pt-2 pb-36">
        {/* --- Profile (kept mounted) --- */}
        <section
          id="panel-profile"
          aria-label="Profile card"
          role="tabpanel"
          aria-labelledby="tab-profile"
          aria-hidden={!isActive("profile")}
          className={[
            cardShell,
            "bg-white/95 dark:bg-content1/70 shadow-lg dark:shadow-[0_6px_20px_rgba(0,0,0,0.25)]",
            !isActive("profile") ? "hidden pointer-events-none" : "",
          ].join(" ")}
          hidden={!isActive("profile")}
          tabIndex={isActive("profile") ? 0 : -1}
        >
          <Card shadow="none" radius="none" className="relative border-0 bg-transparent">
            <CardHeader>
              <div className="flex flex-wrap items-center gap-4">
                <Avatar
                  src={`https://api.dicebear.com/9.x/thumbs/svg?seed=${avatarSeed}&radius=50`}
                  className="h-20 w-20 shadow-md ring-2 ring-neutral-200/70 dark:ring-transparent"
                  alt={`${fullName} avatar`}
                />
                <div className="min-w-0 flex-1">
                  <h1 className="truncate text-2xl font-semibold leading-tight capitalize">
                    {fullName}
                  </h1>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Chip
                      size="sm"
                      variant="flat"
                      className="bg-emerald-100 text-emerald-800 dark:bg-success-200/20 dark:text-success-300"
                    >
                      <span className="flex items-center gap-1.5">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Verified
                      </span>
                    </Chip>

                    <Chip
                      size="sm"
                      variant="flat"
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedTab("connections")}
                      onKeyDown={(e) => e.key === "Enter" && setSelectedTab("connections")}
                      className="cursor-pointer bg-indigo-100 text-indigo-800 dark:bg-primary-200/10 dark:text-primary-400"
                    >
                      <span className="flex items-center gap-1.5">{roleLabel}</span>
                    </Chip>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardBody className="pt-4 pb-8">
              <div className="grid grid-cols-1 gap-3">
                <InfoRow icon={Mail} label="Email">
                  {email}
                </InfoRow>

                <InfoRow icon={User2} label="Full name">
                  {user.firstName || "—"} {user.lastName || "—"}
                </InfoRow>

                <InfoRow icon={CalendarClock} label="Created">
                  {created}
                </InfoRow>
              </div>

              <Divider className="my-5 border-neutral-200 dark:border-default-200/40" />

              {/* action bar */}
              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="flat"
                  className="justify-start gap-2 bg-neutral-100 text-neutral-800 hover:bg-neutral-200 dark:bg-default-100/10 dark:text-foreground"
                >
                  <User2 className="h-4 w-4" />
                  Edit Profile
                </Button>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="hidden sm:inline text-sm text-neutral-500 dark:opacity-70">
                      Theme
                    </span>
                    <ThemeSwitch aria-label="Toggle theme" />
                  </div>
                  <Chip
                    size="sm"
                    variant="flat"
                    className="bg-neutral-100 text-neutral-700 dark:bg-default-200/10 dark:text-default-400"
                  >
                    v1.0.0
                  </Chip>
                </div>
              </div>
            </CardBody>
          </Card>
        </section>

        {/* --- Connections (kept mounted, same card shell) --- */}
        <section
          id="panel-connections"
          aria-label="Connections"
          role="tabpanel"
          aria-labelledby="tab-connections"
          aria-hidden={!isActive("connections")}
          className={[
            cardShell,
            "bg-white/95 dark:bg-content1/70 shadow-lg dark:shadow-none",
            !isActive("connections") ? "hidden pointer-events-none" : "",
          ].join(" ")}
          hidden={!isActive("connections")}
          tabIndex={isActive("connections") ? 0 : -1}
        >
          <Card shadow="none" radius="none" className="relative border-0 bg-transparent">
            {/* Optional header: <CardHeader><h2 className="text-lg font-semibold">Connections</h2></CardHeader> */}
            <CardBody className="p-1 pt-4 pb-8">
              <MyConnectionsComp frameless maxHeight="60vh" />
            </CardBody>
          </Card>
        </section>
      </div>

      {/* Bottom bar */}
      <div className="fixed left-1/2 -translate-x-1/2 bottom-[100px] z-[50] w-full ps-4 pe-4 pointer-events-none">
        <div className="flex justify-between ps-4 pe-4 pointer-events-auto">
          <div>
            <TabsComp
              items={tabs}
              selectedKey={selectedTab}
              defaultKey="profile"
              onChange={(key) => setSelectedTab(key)}
            />
          </div>

          <div>
            {/* <SignOutButton> */}
              <Button
                onPress={() => {setShowLogout(true)}}
                color="danger"
                variant="flat"
                aria-label="Log out"
                size="md"
                radius="full"
                isIconOnly
              >
                <LogOut className="h-4 w-4"/>
              </Button>
            {/* </SignOutButton> */}
          </div>
        </div>
      </div>
    <LogoutDrawer show={showLogOut} onResp={onLogoutDrawerResp}/>
    </DefaultLayout>
  );
}